import React, { useEffect, useState } from "react";
import {
  Flex,
  Menu,
  MenuButton,
  Heading,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Button,
  Image,
  Tooltip,
  Badge,
  Box,
  Text,
  Spinner,
  useDisclosure,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Th,
  Td,
  Tr,
  Modal,
} from "@chakra-ui/react";
import { BellIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import DummyProfile from "../../assets/Images/Hacker-Profile-Image.svg";
import ArrowIcon from "../../assets/Images/Arrow-icon.svg";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import * as routesNames from "../../constant/routes";
import { baseUrl } from "../../hooks/useFetch";
import { showDate, showTime } from "../../utilities/helper";
import FollowUpEditModal from "./FollowUpEditModal";
import MeetingEditModal from "./MeetingEditModal";
import TaskNotificationDetails, {
  getTaskPriorityStyle,
  getTaskStatusStyle,
} from "./TaskNotificationDetails";

const Navbar = () => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const { logout, userName, loginObj } = useAuth();
  const [notificationsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedType, setSelectedType] = useState("");
  const [notificationDetails, setNotificationDetails] = useState<any[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  const [isFollowUpEditOpen, setIsFollowUpEditOpen] = useState(false);
  const [isMeetingEditOpen, setIsMeetingEditOpen] = useState(false);
  const [isTaskDetailsOpen, setIsTaskDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [notificationData, setNotificationData] = useState({
    todayPendingFollowups: 0,
    todayMeetings: 0,
    overdueFollowups: 0,
    overdueMeetings: 0,
    leadNotifications: 0,
    assignedTasksCount: 0,
    worklogPendingReviewCount: 0,
    reviewReminderCount: 0,
    assigneeNotificationsCount: 0,
    reviewerNotificationsCount: 0,
    taskNotifications: 0,
    totalNotifications: 0,
  });

  const formatMeetingDate = (date: string) => {
    if (!date) return "-";
    const [year, month, day] = date.split("T")[0].split("-");
    return `${day}-${month}-${year}`;
  };

  const handleLogout = () => {
    logout();
  };

  const fetchNotifications = async () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(
        `${baseUrl}Notification?timeZone=${encodeURIComponent(timezone)}`,
        {
          headers: {
            Authorization: `Bearer ${loginObj?.token || ""}`,
          },
        }
      );

      const data = await response.json();
      setNotificationData(data);
    } catch (error) {
      console.error("Notification Error:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const notifications = [
    notificationData.todayPendingFollowups > 0 && {
      type: "todayfollowups",
      message: `${notificationData.todayPendingFollowups} pending reminder(s) today`,
      icon: "📞",
    },

    notificationData.todayMeetings > 0 && {
      type: "todaymeetings",
      message: `${notificationData.todayMeetings} meeting(s) scheduled today`,
      icon: "📅",
    },

    notificationData.overdueFollowups > 0 && {
      type: "overduefollowups",
      message: `${notificationData.overdueFollowups} overdue reminder(s)`,
      icon: "⚠️",
    },

    notificationData.overdueMeetings > 0 && {
      type: "overduemeetings",
      message: `${notificationData.overdueMeetings} overdue meeting(s)`,
      icon: "🚨",
    },

    notificationData.assignedTasksCount > 0 && {
      type: "assignedtasks",
      message: `${notificationData.assignedTasksCount} assigned task(s)`,
      icon: "📋",
    },

    notificationData.worklogPendingReviewCount > 0 && {
      type: "worklogpendingreview",
      message: `${notificationData.worklogPendingReviewCount} worklog(s) pending review`,
      icon: "⏳",
    },

    notificationData.reviewReminderCount > 0 && {
      type: "reviewreminder",
      message: `${notificationData.reviewReminderCount} review reminder(s)`,
      icon: "🔔",
    },

    ((notificationData as any).worklogMissingCount > 0 ||
      (notificationData as any).missingWorklogsCount > 0) && {
      type: "worklogmissing",
      message: `${(notificationData as any).worklogMissingCount ||
        (notificationData as any).missingWorklogsCount
        } missing worklog(s)`,
      icon: "⚠️",
    },

    notificationData.assigneeNotificationsCount > 0 && {
      type: "assigneetasks",
      message: `${notificationData.assigneeNotificationsCount} task notification(s)`,
      icon: "📋",
    },

    notificationData.reviewerNotificationsCount > 0 && {
      type: "reviewertasks",
      message: `${notificationData.reviewerNotificationsCount} review notification(s)`,
      icon: "🔍",
    },

    (notificationData as any).allTaskNotificationsCount > 0 && {
      type: "alltasknotifications",
      message: `${(notificationData as any).allTaskNotificationsCount} all task notification(s)`,
      icon: "📋",
    },
  ].filter(Boolean);

  const isTaskNotificationType = (type: string) => {
    const t = (type || "").toLowerCase().trim();
    return [
      "assignedtasks",
      "assignedtask",
      "taskassigned",
      "taskassignments",
      "worklogmissing",
      "worklogpending",
      "missingworklogs",
      "worklogpendingreview",
      "worklogawaitingreview",
      "pendingreview",
      "worklogreview",
      "reviewreminder",
      "reviewreminders",
      "actionrequired",
      "worklogreviewoverdue",
      "overdueworklogs",
      "alltasknotifications",
      "tasknotifications",
      "alltasks",
      "assigneetasks",
      "reviewertasks",
    ].includes(t);
  };

  const getModalTitle = (type: string) => {
    switch ((type || "").toLowerCase().trim()) {
      case "todaymeetings":
        return "Today's Meetings";
      case "overduemeetings":
        return "Overdue Meetings";
      case "todayfollowups":
        return "Today's Reminders";
      case "overduefollowups":
        return "Overdue Reminders";
      case "assignedtasks":
      case "assignedtask":
      case "taskassigned":
      case "taskassignments":
        return "Today's Assigned Tasks";
      case "worklogpendingreview":
      case "worklogawaitingreview":
      case "pendingreview":
      case "worklogreview":
        return "Worklogs Pending Review";
      case "reviewreminder":
      case "reviewreminders":
      case "actionrequired":
      case "worklogreviewoverdue":
      case "overdueworklogs":
        return "Review Reminders";
      case "worklogmissing":
      case "worklogpending":
      case "missingworklogs":
        return "Missing Worklogs";
      case "alltasknotifications":
      case "tasknotifications":
      case "alltasks":
        return "All Task Notifications";
      case "assigneetasks":
        return "Assigned Task Notifications";
      case "reviewertasks":
        return "Reviewer Task Notifications";
      default:
        return "Notification Details";
    }
  };

  const loadNotificationDetails = async (type: string) => {
    try {
      setDetailsLoading(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(
        `${baseUrl}Notification/NotificationDetails?type=${type}&timeZone=${encodeURIComponent(
          timezone
        )}`,
        {
          headers: {
            Authorization: `Bearer ${loginObj?.token || ""}`,
          },
        }
      );

      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      setNotificationDetails(list);
      setSelectedType(type);
      onOpen();
    } catch (error) {
      console.error("Error loading notification details:", error);
      setNotificationDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const isTaskType = isTaskNotificationType(selectedType);

  return (
    <>
      <Flex
        h="50px"
        align="center"
        px={6}
        justifyContent="flex-end"
        bg="#FFFFFF"
        pt={2}
      >
        <Flex align="center" gap={5}>
          {/* NOTIFICATION */}
          <Menu>
            <Tooltip label="Notifications" placement="bottom">
              <MenuButton
                as={Button}
                variant="ghost"
                position="relative"
                p={0}
                minW="unset"
                h="40px"
                w="40px"
                borderRadius="full"
                bg="#F5F5FA"
                _hover={{ bg: "#ECECF3" }}
              >
                <BellIcon boxSize={5} color="#6B7280" />

                {notificationData.totalNotifications > 0 && (
                  <Badge
                    colorScheme="red"
                    borderRadius="full"
                    position="absolute"
                    top="-2px"
                    right="-2px"
                    fontSize="10px"
                  >
                    {notificationData.totalNotifications}
                  </Badge>
                )}
              </MenuButton>
            </Tooltip>

            <MenuList
              w="360px"
              maxH="300px"
              overflowY="auto"
              borderRadius="14px"
              border="1px solid #E5E7EB"
              boxShadow="0px 10px 30px rgba(0,0,0,0.08)"
            >
              {notificationsLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner />
                </Flex>
              ) : notifications.length === 0 ? (
                <Box p={4}>
                  <Text textAlign="center" color="gray.500">
                    No new notifications
                  </Text>
                </Box>
              ) : (
                notifications.map((n: any, idx) => (
                  <MenuItem
                    key={idx}
                    py={3}
                    borderRadius="10px"
                    _hover={{ bg: "#F9FAFB" }}
                    onClick={() => loadNotificationDetails(n.type)}
                  >
                    <Text fontSize="14px" fontWeight="500">
                      {n.icon} {n.message}
                    </Text>
                  </MenuItem>
                ))
              )}
            </MenuList>
          </Menu>

          {/* DIVIDER */}
          <Box h="42px" w="1px" bg="#E5E7EB" />

          {/* PROFILE */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              p={0}
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              rightIcon={<Image src={ArrowIcon} />}
            >
              <Flex align="center" gap={3}>
                <Avatar size="sm" src={avatarUrl || DummyProfile} />

                <Text
                  fontFamily="inter"
                  fontSize="18px"
                  fontWeight="500"
                  color="#111827"
                >
                  {userName}
                </Text>
              </Flex>
            </MenuButton>

            <MenuList borderRadius="14px">
              <MenuItem as={NavLink} to={routesNames.PROFILE}>
                Profile
              </MenuItem>

              <MenuDivider />
              <MenuItem as={NavLink} to={routesNames.UpdatePassword}>
                Update Password
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      {/* NOTIFICATION LIST MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />

        <ModalContent maxH="650px" overflowY="auto" borderRadius="16px">
          <ModalHeader
            bg="#F8FAFC"
            borderBottom="1px solid #E2E8F0"
            py={4}
            fontSize="18px"
            fontWeight="700"
            color="#1E293B"
          >
            {getModalTitle(selectedType)}
          </ModalHeader>
          <ModalCloseButton top="14px" right="16px" />
          <ModalBody p={5}>
            {detailsLoading ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" color="#4048D6" thickness="3px" />
              </Flex>
            ) : isTaskType ? (
              /* TASK & WORKLOG NOTIFICATIONS TABLE */
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="#F8FAFC">
                    <Tr>
                      <Th color="#475467" py={3}>S. No.</Th>
                      <Th color="#475467">Task Name</Th>
                      <Th color="#475467">Project</Th>
                      <Th color="#475467">Service</Th>
                      <Th color="#475467">Milestone</Th>

                      <Th color="#475467">Assigned To</Th>
                      <Th color="#475467">Priority</Th>
                      <Th color="#475467">Status</Th>
                      <Th color="#475467">Date & Time</Th>
                      {/* <Th color="#475467" textAlign="center">Action</Th> */}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {notificationDetails.length === 0 ? (
                      <Tr>
                        <Td colSpan={9} textAlign="center" py={8} color="gray.500">
                          No notifications found.
                        </Td>
                      </Tr>
                    ) : (
                      notificationDetails.map((item: any, index: number) => {
                        const taskId =
                          item.taskId ?? item.taskManagementTaskId ?? item.projectTaskId;
                        const taskName =
                          item.taskName ??
                          item.projectTaskName ??
                          item.title ??
                          item.TaskName ??
                          "Task";
                        const projectName =
                          item.projectName ?? item.ProjectName ?? "-";
                        const serviceName =
                          item.serviceName ?? item.ServiceName ?? "";
                        const milestoneName =
                          item.milestoneName ?? item.MilestoneName ?? "";
                        const assignedToName =
                          item.assignedToName ??
                          item.AssignedToName ??
                          item.assigneeName ??
                          "-";
                        const priorityName =
                          item.priorityName ??
                          item.PriorityName ??
                          item.priority ??
                          "Normal";
                        const statusName =
                          item.statusName ??
                          item.StatusName ??
                          item.status ??
                          "Pending";
                        const taskDate =
                          item.taskDate ??
                          item.date ??
                          item.startDate ??
                          item.TaskDate ??
                          item.Date ??
                          "";
                        const startTime =
                          item.startTime ?? item.StartTime ?? "";
                        const endTime = item.endTime ?? item.EndTime ?? "";

                        const priorityStyle = getTaskPriorityStyle(priorityName);
                        const statusStyle = getTaskStatusStyle(statusName);

                        return (
                          <Tr
                            key={taskId || index}
                            _hover={{ bg: "#F8FAFC" }}
                            cursor="pointer"
                            onClick={() => {
                              setSelectedTask(item);
                              setSelectedTaskId(taskId);
                              setIsTaskDetailsOpen(true);
                            }}
                          >
                            <Td fontSize="13px">{index + 1}</Td>
                            <Td
                              fontSize="13px"
                              fontWeight="600"
                              color="#1E293B"
                              maxW="220px"
                              isTruncated
                              title={taskName}
                            >
                              {taskName}
                            </Td>
                            <Td fontSize="13px" color="#4B5563">
                              {projectName}
                            </Td>
                            <Td fontSize="13px" color="#4B5563">
                              {serviceName ? (
                                <Text fontSize="12px">
                                  {serviceName}

                                </Text>
                              ) : (
                                "-"
                              )}
                            </Td>

                            <Td fontSize="13px" color="#4B5563">
                              {milestoneName ? (
                                <Text fontSize="12px">{milestoneName}</Text>) : (
                                "-"
                              )}
                            </Td>
                            <Td fontSize="13px" color="#374151">
                              {assignedToName}
                            </Td>
                            <Td>
                              <Badge
                                bg={priorityStyle.bg}
                                color={priorityStyle.color}
                                border="1px solid"
                                borderColor={priorityStyle.border}
                                fontSize="11px"
                                px={2.5}
                                py={0.5}
                                borderRadius="full"
                                fontWeight="600"
                              >
                                {priorityName}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge
                                bg={statusStyle.bg}
                                color={statusStyle.color}
                                border="1px solid"
                                borderColor={statusStyle.border}
                                fontSize="11px"
                                px={2.5}
                                py={0.5}
                                borderRadius="full"
                                fontWeight="600"
                              >
                                {statusName}
                              </Badge>
                            </Td>
                            <Td fontSize="12px" color="#4B5563">
                              {taskDate ? showDate(taskDate) : "-"}
                              {startTime
                                ? ` (${startTime}${endTime ? ` - ${endTime}` : ""
                                })`
                                : ""}
                            </Td>
                            {/* <Td
                              textAlign="center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="indigo"
                                color="#4048D6"
                                borderColor="#C7D2FE"
                                _hover={{ bg: "#EEF2FF" }}
                                leftIcon={<ViewIcon />}
                                borderRadius="6px"
                                onClick={() => {
                                  setSelectedTask(item);
                                  setSelectedTaskId(taskId);
                                  setIsTaskDetailsOpen(true);
                                }}
                              >
                                Details
                              </Button>
                            </Td> */}
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              /* LEAD MEETINGS & FOLLOWUPS TABLE */
              <Table size="sm">
                <Thead bg="#F8FAFC">
                  <Tr>
                    <Th py={3}>S. No.</Th>
                    <Th>Lead Name</Th>
                    <Th>Email</Th>
                    <Th>Number</Th>
                    <Th>Title / Description</Th>
                    <Th>Date</Th>
                    <Th>Time</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {notificationDetails.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                        No notifications found.
                      </Td>
                    </Tr>
                  ) : (
                    notificationDetails.map((item: any, index: number) => (
                      <Tr key={item.id || index} _hover={{ bg: "#F8FAFC" }}>
                        <Td>{index + 1}</Td>
                        <Td>{item.pocname}</Td>
                        <Td>{item.pocemail}</Td>
                        <Td>{item.pocNUMBER}</Td>

                        <Td
                          dangerouslySetInnerHTML={{
                            __html:
                              item.meetingTitle || item.description || "",
                          }}
                        />
                        <Td>
                          {item.meetingDate
                            ? item.isAllDay
                              ? formatMeetingDate(item.meetingDate)
                              : showDate(item.startDateTime)
                            : showDate(item.followTime)}
                        </Td>

                        <Td>
                          {item.meetingDate
                            ? item.isAllDay
                              ? "-"
                              : showTime(item.startDateTime)
                            : showTime(item.followTime)}
                        </Td>
                        <Td>
                          <EditIcon
                            cursor="pointer"
                            color="#4048D6"
                            onClick={() => {
                              setSelectedLeadId(item.leadId);
                              setSelectedId(item.id);
                              onClose();
                              if (
                                selectedType === "todayfollowups" ||
                                selectedType === "overduefollowups"
                              ) {
                                setIsFollowUpEditOpen(true);
                              } else {
                                setIsMeetingEditOpen(true);
                              }
                            }}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* LEAD FOLLOW UP EDIT MODAL */}
      {selectedLeadId !== null && selectedId !== null && isFollowUpEditOpen && (
        <FollowUpEditModal
          isOpen={isFollowUpEditOpen}
          onClose={() => {
            setIsFollowUpEditOpen(false);
            setSelectedLeadId(null);
            setSelectedId(null);
            onOpen();
          }}
          leadId={selectedLeadId}
          followUpId={selectedId}
          onFollowUpSaved={() => {
            fetchNotifications();
            loadNotificationDetails(selectedType);
          }}
        />
      )}

      {/* LEAD MEETING EDIT MODAL */}
      {selectedLeadId !== null && selectedId !== null && isMeetingEditOpen && (
        <MeetingEditModal
          isOpen={isMeetingEditOpen}
          onClose={() => {
            setIsMeetingEditOpen(false);
            setSelectedLeadId(null);
            setSelectedId(null);
            onOpen();
          }}
          leadId={selectedLeadId}
          meetingId={selectedId}
          onMeetingSaved={() => {
            fetchNotifications();
            loadNotificationDetails(selectedType);
          }}
        />
      )}

      {/* TASK NOTIFICATION DETAILS MODAL */}
      <TaskNotificationDetails
        isOpen={isTaskDetailsOpen}
        onClose={() => {
          setIsTaskDetailsOpen(false);
          setSelectedTask(null);
          setSelectedTaskId(null);
        }}
        taskId={selectedTaskId}
        taskData={selectedTask}
        onTaskSaved={() => {
          fetchNotifications();
          loadNotificationDetails(selectedType);
        }}
      />
    </>
  );
};

export default Navbar;